/**
 * History: what the watcher found that is over - ended, dismissed, or left
 * behind when its rule was stopped or changed. The Inbox keeps only what is
 * open; this is everything else, filterable, and quiet.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { canDo } from '../../state/store.ts';
import { getArgusHistory } from '../../api/endpoints.ts';
import type { ArgusHistory, ArgusInboxItem } from '../../api/types.ts';
import { setArgusRoom, openArgusEpisode } from './argus-lens.ts';
import { renderEpisodeMenu, renderEpisodeTable, verdictWords } from './argus-episodes.ts';
import { inboxRuleFilter, markEpisode, rulesRoomOpenRule } from './argus-inbox-store.ts';
import { loadWatchers } from './argus-rules-store.ts';
import { userMessage } from '../../api/errors.ts';

type Outcome = 'all' | 'judged' | 'closed' | 'snoozed' | 'dismissed' | 'unwatched';
const SPANS = [7, 30, 90] as const;
const PAGE = 100;

@customElement('argus-history-room')
export class ArgusHistoryRoom extends LightElement {
  // The rule filter is shared with the Inbox: "only this rule" follows you.
  private store = new StoreController(this, [inboxRuleFilter]);

  @state() private data: ArgusHistory | null = null;
  @state() private failed: string | null = null;
  @state() private days: number = 30;
  @state() private outcome: Outcome = 'all';
  @state() private query = '';
  @state() private shown = PAGE;
  @state() private menu: { item: ArgusInboxItem; x: number; y: number } | null = null;
  private asked = 0;

  override connectedCallback(): void {
    super.connectedCallback();
    void loadWatchers();
    void this.load();
    document.addEventListener('pointerdown', this.onOutside, true);
    document.addEventListener('keydown', this.onKey);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('pointerdown', this.onOutside, true);
    document.removeEventListener('keydown', this.onKey);
  }

  private onOutside = (e: Event): void => {
    if (this.menu && !(e.target as Element | null)?.closest('.cat-menu, .argus-ibx-more')) this.menu = null;
  };

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.menu = null;
  };

  private async load(): Promise<void> {
    const ask = ++this.asked;
    try {
      const answer = await getArgusHistory(inboxRuleFilter.value?.id ?? null, this.days);
      if (ask !== this.asked) return;
      this.data = answer;
      this.failed = null;
    } catch (error) {
      if (ask !== this.asked) return;
      this.failed = userMessage(error, 'History could not be read.');
    }
  }

  private visible(item: ArgusInboxItem): boolean {
    if (this.outcome !== 'all' && item.state !== this.outcome) return false;
    const q = this.query.trim().toLowerCase();
    if (!q) return true;
    return [
      item.rule_name,
      item.entity_name,
      item.sentence ?? '',
      item.marked_by_name ?? '',
      item.judged_by_name ?? '',
      item.verdict_note ?? '',
      verdictWords(item),
    ]
      .some((text) => text.toLowerCase().includes(q));
  }

  private chip(label: string, on: boolean, run: () => void, count?: number): TemplateResult {
    return html`<button type="button" class="argus-fchip ${on ? 'is-on' : ''}" aria-pressed=${on ? 'true' : 'false'} @click=${run}>
      ${label}${count != null ? html`<b>${count}</b>` : nothing}
    </button>`;
  }

  private renderToolbar(all: ArgusInboxItem[]): TemplateResult {
    const count = (o: Outcome): number => (o === 'all' ? all.length : all.filter((i) => i.state === o).length);
    const outcome = (o: Outcome, label: string): TemplateResult =>
      this.chip(label, this.outcome === o, () => {
        this.outcome = o;
        this.shown = PAGE;
      }, count(o));
    const filter = inboxRuleFilter.value;
    return html`<div class="argus-toolbar">
      <input
        class="argus-search"
        type="search"
        placeholder="Find a rule, a ship, a person…"
        aria-label="Find in history"
        .value=${this.query}
        @input=${(e: Event) => {
          this.query = (e.target as HTMLInputElement).value;
          this.shown = PAGE;
        }}
      />
      ${outcome('all', 'All')} ${outcome('judged', 'Judged')} ${outcome('closed', 'Ended')}
      ${count('snoozed') ? outcome('snoozed', 'Muted') : nothing}
      ${count('dismissed') ? outcome('dismissed', 'Dismissed') : nothing}
      ${count('unwatched') ? outcome('unwatched', 'No longer watched') : nothing}
      <span class="argus-toolbar-gap" aria-hidden="true"></span>
      ${SPANS.map((d) =>
        this.chip(`${d} days`, this.days === d, () => {
          this.days = d;
          this.shown = PAGE;
          void this.load();
        }),
      )}
      ${filter
        ? html`<span class="argus-toolbar-gap" aria-hidden="true"></span>${this.chip(`Only: ${filter.name} ×`, true, () => {
            inboxRuleFilter.set(null);
            void this.load();
          })}`
        : nothing}
    </div>`;
  }

  override render(): TemplateResult {
    const all = this.data?.episodes ?? [];
    const rows = all.filter((i) => this.visible(i));
    const onMenu = (item: ArgusInboxItem, x: number, y: number): void => {
      this.menu = { item, x, y };
    };
    return html`
      <div class="argus-ibx">
        <div class="argus-ibx-head">
          <h2 class="argus-ibx-title">History</h2>
          <span class="argus-lfaint">what the watcher found that is over</span>
        </div>
        ${this.renderToolbar(all)}
        ${this.failed
          ? html`<p class="argus-room-waiting"><span class="argus-room-waiting-label">Not readable</span>${this.failed}</p>`
          : !this.data
            ? html`<p class="argus-room-waiting">Reading history…</p>`
            : !this.data.available
              ? html`<p class="argus-room-waiting">This database has never heard from a watcher.</p>`
              : rows.length
                ? html`${renderEpisodeTable(rows.slice(0, this.shown), false, onMenu, {
                      onOpen: (item) => openArgusEpisode(item.id),
                    })}
                    ${rows.length > this.shown
                      ? html`<button type="button" class="argus-fchip" @click=${() => (this.shown += PAGE)}>
                          Show ${Math.min(PAGE, rows.length - this.shown)} more of ${rows.length - this.shown}
                        </button>`
                      : nothing}`
                : html`<p class="argus-ibx-quiet"><span>Nothing here in the last ${this.days} days${this.query || this.outcome !== 'all' ? ' that matches' : ''}.</span></p>`}
      </div>
      ${renderEpisodeMenu(this.menu, {
        mark: canDo('argus:triage')
          ? (item, mark) => {
              this.menu = null;
              void markEpisode(item.id, mark).then(() => this.load());
            }
          : undefined,
        openRule: (item) => {
          this.menu = null;
          rulesRoomOpenRule.set(item.rule_id);
          setArgusRoom('rules');
        },
        filter: (item) => {
          this.menu = null;
          inboxRuleFilter.set(item ? { id: item.rule_id, name: item.rule_name } : null);
          void this.load();
        },
        filtered: !!inboxRuleFilter.value,
      })}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'argus-history-room': ArgusHistoryRoom;
  }
}
